import React, { useEffect, useRef, useState } from "react";
import { Camera, Image as ImageIcon, Loader2, RotateCcw, X } from "lucide-react";
import { compress } from "@/components/PhotoUpload";
import { isNativePlatform } from "@/lib/native";
import { holdTabletLock, releaseTabletLock, isCurrentTabletHold } from "@/lib/tabletHold";

const safeCompress = (file) => Promise.race([
  compress(file).then((f) => (f && f.size ? f : file)).catch(() => file),
  new Promise((r) => setTimeout(() => r(file), 8000)),
]);

const toBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onloadend = () => resolve(String(r.result || "").split(",").pop());
  r.onerror = () => reject(new Error("read-failed"));
  r.readAsDataURL(blob);
});

// Uploads with the PIN-verified person's own session (never the shared device or a personal login).
async function uploadWith(client, file, onProgress, signal, valid) {
  if (isNativePlatform()) {
    const base64 = await toBase64(file);
    if (!valid()) throw new Error("session-ended");
    const { data } = await client.post("/upload/native", { filename: file.name || "photo.jpg", data: base64 }, { signal, timeout: 90000 });
    return data;
  }
  const fd = new FormData();
  fd.append("file", file, file.name || "photo.jpg");
  const { data } = await client.post("/upload", fd, {
    signal, timeout: 90000,
    onUploadProgress: (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
  });
  return data;
}

function PickButton({ testid, icon: Icon, label, capture, onFile, onPick, onCancel, disabled }) {
  // A visible, real file input covers the button: Android WebView / Silk open it on a direct tap,
  // unlike a display:none input triggered programmatically.
  return (
    <label data-testid={testid} className={`relative flex-1 min-h-[64px] rounded-2xl flex items-center justify-center gap-2 font-semibold overflow-hidden ${disabled ? "bg-slate-800 text-slate-500" : "bg-slate-800 text-white active:bg-brand/30"}`}>
      <Icon size={22} /> {label}
      <input type="file" accept="image/*" {...(capture ? { capture: "environment" } : {})} disabled={disabled}
        data-testid={`${testid}-input`} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        onClick={(e) => { e.target.value = ""; onPick(); }}
        onCancel={onCancel}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); else onCancel(); }} />
    </label>
  );
}

export function TabletPhotoCapture({ client, onUploaded, required }) {
  const [state, setState] = useState({ phase: "idle" }); // idle | uploading | done | error
  const [preview, setPreview] = useState(null);
  const ctrl = useRef(null);
  const alive = useRef(true);
  const lastFile = useRef(null);
  const lease = useRef(null);
  const picking = useRef(false);
  const deadlineTimer = useRef(null);
  const cancelPick = () => {
    picking.current = false;
    releaseTabletLock(lease.current);
    lease.current = null;
    clearTimeout(deadlineTimer.current);
  };
  const begin = () => {
    lease.current = holdTabletLock();
    picking.current = true;
    const op = lease.current;
    if (!op) return;
    clearTimeout(deadlineTimer.current);
    deadlineTimer.current = setTimeout(() => isCurrentTabletHold(op),
      Math.max(0, op.deadline - Date.now()));
  };

  useEffect(() => {
    alive.current = true; // StrictMode re-runs effects: a previous cleanup must not leave this false
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      const op = lease.current;
      if (!isCurrentTabletHold(op)) return;
      // Some pickers report cancellation only by returning focus, not a cancel event.
      setTimeout(() => {
        if (alive.current && picking.current && lease.current === op) cancelPick();
      }, 1500);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive.current = false; ctrl.current?.abort(); clearTimeout(deadlineTimer.current); releaseTabletLock(lease.current); document.removeEventListener("visibilitychange", onVis); };
  }, []);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const start = async (file) => {
    const op = lease.current;
    picking.current = false;
    if (!alive.current || !isCurrentTabletHold(op)) return; // a late picker result after Finish / lock is dropped
    lastFile.current = file;
    onUploaded(null);
    ctrl.current?.abort();
    ctrl.current = new AbortController();
    setPreview(URL.createObjectURL(file));
    setState({ phase: "uploading", pct: 0 });
    // Keep the picker deadline: starting an upload must not extend it.
    try {
      const small = await safeCompress(file);
      const valid = () => alive.current && isCurrentTabletHold(op);
      if (!valid()) return;
      const data = await uploadWith(client, small, (pct) => valid() && setState({ phase: "uploading", pct }), ctrl.current.signal, valid);
      if (!valid()) return;
      if (!data?.file_id) throw new Error("no-id");
      setState({ phase: "done" });
      onUploaded(data.file_id);
    } catch (e) {
      if (!alive.current || !isCurrentTabletHold(op) || e?.name === "CanceledError") return;
      const st = e?.response?.status;
      setState({ phase: "error", msg: st === 400 ? (e.response.data?.detail || "This file can't be used. Try another photo.")
        : st === 401 ? "Your session ended. Sign in with your PIN again."
        : "Upload failed — check the Wi-Fi and try again. The task stays open until the photo uploads." });
    } finally {
      clearTimeout(deadlineTimer.current);
      releaseTabletLock(op);
    }
  };
  const clear = () => { ctrl.current?.abort(); lastFile.current = null; cancelPick(); setPreview(null); setState({ phase: "idle" }); onUploaded(null); };
  const busy = state.phase === "uploading";

  return (
    <div data-testid="tablet-photo-capture" className="space-y-3">
      {required && <p className="text-sm text-amber-300" data-testid="tablet-photo-required">Photo evidence is required for this task.</p>}
      {preview && (
        <div className="relative rounded-2xl overflow-hidden border border-slate-700 h-48 bg-slate-800">
          <img src={preview} alt="Cleaning evidence preview" className="w-full h-full object-cover" data-testid="tablet-photo-preview" />
          {!busy && <button type="button" onClick={clear} data-testid="tablet-photo-remove" className="absolute top-2 right-2 bg-slate-950/80 rounded-full p-2"><X size={18} /></button>}
        </div>
      )}
      {busy && (
        <div data-testid="tablet-photo-progress">
          <div className="flex items-center gap-2 text-sm text-slate-300"><Loader2 size={16} className="animate-spin" /> Uploading photo{state.pct ? ` ${state.pct}%` : "…"}</div>
          <div className="h-2 rounded bg-slate-800 mt-2 overflow-hidden"><div className="h-full bg-brand transition-[width]" style={{ width: `${state.pct || 8}%` }} /></div>
        </div>
      )}
      {state.phase === "done" && <p className="text-sm text-emerald-300" data-testid="tablet-photo-uploaded">Photo uploaded</p>}
      {state.phase === "error" && (
        <div className="rounded-xl bg-red-500/15 border border-red-500/40 p-3 text-sm text-red-200" data-testid="tablet-photo-error">
          {state.msg}
          {lastFile.current && <button type="button" onClick={() => { begin(); start(lastFile.current); }} data-testid="tablet-photo-retry" className="mt-2 flex items-center gap-1 text-red-100 underline"><RotateCcw size={14} /> Try upload again</button>}
        </div>
      )}
      <div className="flex gap-3">
        <PickButton testid="tablet-photo-take" icon={Camera} label={preview ? "Retake photo" : "Take photo"} capture onFile={start} onPick={begin} onCancel={cancelPick} disabled={busy} />
        <PickButton testid="tablet-photo-choose" icon={ImageIcon} label="Choose from device" onFile={start} onPick={begin} onCancel={cancelPick} disabled={busy} />
      </div>
    </div>
  );
}
